/**
 * Session State Service - Normalized Table Operations
 * 
 * This service handles session state using normalized relational tables
 * instead of JSONB blobs. It provides granular updates and eliminates
 * race conditions that occur with full-state replacements.
 * 
 * Used when sessions.use_normalized_state = true
 *
 * Key exports:
 *   updateSingleCourtAtomic  – DB-level OCC write for one court (preferred for live writes)
 *   updateCourtAssignments   – Full snapshot for a court type (used on load/restore only)
 *   updateWaitingList        – Full snapshot of the waiting list
 */

import { supabase } from '../utils/supabase';
import { persistDebugLog, persistDebugWarn } from '../utils/sessionPersistDebug';
import { normalizeMatchStartTime } from '../utils/matchTimerUtils';

// ============================================================================
// OPTIMISTIC CONCURRENCY CONTROL (OCC)
// ============================================================================

/**
 * Thrown by updateSingleCourtAtomic when the DB version no longer matches
 * the version the caller last read (i.e. another host already wrote).
 * Callers should re-fetch the court row and retry.
 */
export class CourtConflictError extends Error {
  constructor(currentVersion) {
    super(`Court write conflict: DB version is now ${currentVersion}`);
    this.name = 'CourtConflictError';
    this.currentVersion = currentVersion;
  }
}

/**
 * Thrown when claim_session_court_score_submission rejects (multi-tab / multi-host
 * trying to record the same in-flight match twice).
 */
export class ScoreSubmissionClaimError extends Error {
  constructor(reason, payload = {}) {
    const msg =
      typeof payload.message === 'string' && payload.message.length > 0
        ? payload.message
        : ScoreSubmissionClaimError.defaultMessage(reason);
    super(msg);
    this.name = 'ScoreSubmissionClaimError';
    this.reason = reason;
    this.currentVersion = payload.current_version;
  }

  static defaultMessage(reason) {
    switch (reason) {
      case 'not_in_progress':
        return 'This match was already recorded on another device.';
      case 'version_mismatch':
        return 'Court data changed. Wait for sync, then try again if needed.';
      case 'pending_other':
        return 'Another host is already submitting a score for this court.';
      default:
        return 'Unable to submit this score right now. Please try again.';
    }
  }
}

/**
 * General-purpose error for server-side session command failures.
 * All intent RPCs return { ok, code, message } JSON; this class wraps
 * the failure so callers can map codes to toasts without inspecting raw strings.
 *
 * Known codes:
 *   WAITLIST_JOIN_FAILED  – could not add player to waiting list
 *   WAITLIST_LEAVE_FAILED – could not remove player from waiting list
 *   WAITLIST_REORDER_FAILED – reorder rejected (e.g. concurrent edit)
 *   COURT_CONFLICT        – OCC version mismatch on a court write
 *   COURT_ASSIGN_FAILED   – unexpected court assignment error
 *   SET_NEXT_UP_FAILED    – could not update next-up slot
 *   NOT_WRITER            – legacy; lease enforcement retired (CQRS-lite)
 */
export class SessionCommandError extends Error {
  constructor(code, message, extra = {}) {
    super(message);
    this.name = 'SessionCommandError';
    this.code = code;
    Object.assign(this, extra);
  }
}

/**
 * Reserve the right to insert session_games for this in-flight court (DB row lock).
 * Legacy path only — used before finishMatchCmd when REACT_APP_SERVER_MATCHMAKING is false.
 * Server-matchmaking acquires the claim inline in prepare_score_transition (complete-session-score EF).
 * @returns {Promise<{ success: boolean, skipped?: boolean }>}
 * @throws {ScoreSubmissionClaimError} when another submission is in progress or the match is no longer active in the DB
 */
export async function claimSessionCourtScoreSubmission(sessionId, courtNumber, expectedVersion) {
  const { data, error } = await supabase.rpc('claim_session_court_score_submission', {
    p_session_id:        sessionId,
    p_court_number:      courtNumber,
    p_expected_version:  expectedVersion ?? null,
  });
  if (error) {
    console.error('❌ claimSessionCourtScoreSubmission RPC error:', error);
    throw error;
  }
  if (data?.success === false) {
    throw new ScoreSubmissionClaimError(data.reason, data);
  }
  return data;
}

/** Clear claim and mark court not in progress after a successful session_games insert. */
export async function finalizeSessionCourtScoreSubmission(sessionId, courtNumber) {
  const { error } = await supabase.rpc('finalize_session_court_score_submission', {
    p_session_id:   sessionId,
    p_court_number: courtNumber,
  });
  if (error) console.warn('finalizeSessionCourtScoreSubmission:', error);
}

/** Clear claim only (e.g. createGame threw) so the host can retry. */
export async function abortSessionCourtScoreSubmission(sessionId, courtNumber) {
  const { error } = await supabase.rpc('abort_session_court_score_submission', {
    p_session_id:   sessionId,
    p_court_number: courtNumber,
  });
  if (error) console.warn('abortSessionCourtScoreSubmission:', error);
}

/**
 * Atomically update a single court using the DB-level RPC with OCC.
 *
 * The Postgres function (update_court_state_atomic) acquires a row-level lock
 * on session_courts, checks expectedVersion, updates the court + players in one
 * transaction, and bumps the version. If another host wrote first, it returns a
 * conflict payload — we throw CourtConflictError so the caller can retry.
 *
 * @param {string}        sessionId        – session UUID
 * @param {number}        courtNumber      – court number (1-based for active, 0-based for next_up)
 * @param {string}        courtType        – 'active' | 'next_up'
 * @param {number}        expectedVersion  – version from last DB read; -1 to skip OCC check
 * @param {object}        courtData        – { inProgress, team1, team2, gameCreation }
 * @param {string|null}   tabInstanceId    – writer lease tab ID; when provided the RPC verifies the lease
 * @returns {Promise<{newVersion: number}>}
 * @throws {CourtConflictError}            – if another host already modified this court
 */
export async function updateSingleCourtAtomic(
  sessionId,
  courtNumber,
  courtType,
  expectedVersion,
  courtData,
  tabInstanceId = null
) {
  const { inProgress = false, team1 = [], team2 = [], gameCreation = {} } = courtData;

  const { data, error } = await supabase.rpc('update_court_state_atomic', {
    p_session_id:               sessionId,
    p_court_number:             courtNumber,
    p_court_type:               courtType,
    p_expected_version:         expectedVersion ?? -1,
    p_in_progress:              inProgress,
    p_team1_players:            team1.filter(Boolean),
    p_team2_players:            team2.filter(Boolean),
    p_game_creation_origin:     gameCreation?.origin     ?? null,
    p_game_creation_algorithm:  gameCreation?.algorithm  ?? null,
    p_game_creation_altered:    gameCreation?.isAltered  ?? false,
    p_tab_instance_id:          tabInstanceId ?? null,
  });

  if (error) {
    console.error('❌ SessionStateService: updateSingleCourtAtomic RPC error:', error);
    throw error;
  }

  if (data?.conflict) {
    throw new CourtConflictError(data.current_version);
  }

  if (data?.not_writer) {
    console.warn('❌ SessionStateService: updateSingleCourtAtomic — lease not held:', data?.code);
    throw new Error(`NOT_WRITER: ${data?.message ?? 'Writer lease check failed'}`);
  }

  return { newVersion: data.new_version };
}

/**
 * Map session_courts.match_started_at to client court.startTime (ISO string).
 * @param {{ match_started_at?: string|null, in_progress?: boolean }} courtRow
 * @returns {string|null}
 */
function mapCourtMatchStartTime(courtRow) {
  if (!courtRow?.in_progress) return null;
  return normalizeMatchStartTime(courtRow.match_started_at);
}

/**
 * Stable ordering for court roster arrays — prevents partner/side flip after DB reload.
 */
function sortCourtTeamPlayerIds(players, teamNumber) {
  return players
    .filter((p) => p.team_number === teamNumber)
    .sort((a, b) => {
      const posA = a.waitlist_position ?? Number.MAX_SAFE_INTEGER;
      const posB = b.waitlist_position ?? Number.MAX_SAFE_INTEGER;
      if (posA !== posB) return posA - posB;
      return String(a.player_id).localeCompare(String(b.player_id));
    })
    .map((p) => p.player_id);
}

/**
 * Fetch a single court row + its players from the DB and return a court object
 * in the same shape as buildCourtAssignments produces.
 * Used for conflict-retry and granular realtime merge.
 */
export async function fetchSingleCourt(sessionId, courtNumber, courtType) {
  const [courtResult, playersResult] = await Promise.all([
    supabase
      .from('session_courts')
      .select('*')
      .eq('session_id', sessionId)
      .eq('court_number', courtNumber)
      .eq('court_type', courtType)
      .maybeSingle(),
    supabase
      .from('session_players')
      .select('*')
      .eq('session_id', sessionId)
      .eq('court_number', courtNumber)
      .eq('court_type', courtType),
  ]);

  if (courtResult.error) throw courtResult.error;
  if (playersResult.error) throw playersResult.error;

  const court = courtResult.data;
  const players = playersResult.data || [];

  if (!court) return null;

  const team1 = sortCourtTeamPlayerIds(players, 1);
  const team2 = sortCourtTeamPlayerIds(players, 2);

  return {
    courtNumber:    court.court_number,
    team1,
    team2,
    inProgress:     court.in_progress || false,
    startTime:      mapCourtMatchStartTime(court),
    version:        court.version ?? 0,
    gameCreation: {
      origin:       court.game_creation_origin     || null,
      algorithm:    court.game_creation_algorithm  || null,
      isAltered:    court.game_creation_altered    || false,
      scheduledGameId: court.scheduled_game_id || null,
    },
  };
}

/**
 * Lightweight waitlist + wait-metrics slice for post-score targeted refresh
 * (avoids full load_session_normalized_snapshot when courts are fetched per-slot).
 */
export async function fetchSessionWaitlistAndMetrics(sessionId) {
  const [playersResult, sessionResult] = await Promise.all([
    supabase
      .from('session_players')
      .select(
        'player_id, player_list_status, waitlist_position, courts_watched, last_court_update, is_on_cooldown, last_game_completed_at, match_preference, preference_strength, updated_at, joined_at, created_at, court_type, court_number, team_number, status, mens_doubles_eligible, mens_doubles_shadow_elo'
      )
      .eq('session_id', sessionId)
      .eq('status', 'active'),
    supabase
      .from('sessions')
      .select('upcoming_queue_slot_count')
      .eq('id', sessionId)
      .maybeSingle(),
  ]);

  if (playersResult.error) throw playersResult.error;
  if (sessionResult.error) throw sessionResult.error;

  const playersArr = playersResult.data || [];
  const waitlistIds = playersArr
    .filter((p) => p.player_list_status === 'waitlist')
    .sort((a, b) => (a.waitlist_position ?? 999999) - (b.waitlist_position ?? 999999))
    .map((p) => p.player_id);
  const waitlistSet = new Set(waitlistIds);
  const orphanIds = playersArr
    .filter(isOrphanedSessionPlayer)
    .filter((p) => !waitlistSet.has(p.player_id))
    .sort(
      (a, b) =>
        new Date(a.joined_at || a.created_at || 0) -
        new Date(b.joined_at || b.created_at || 0)
    )
    .map((p) => p.player_id);

  return {
    waitingList: [...waitlistIds, ...orphanIds],
    playerWaitMetrics: buildPlayerWaitMetrics(playersArr),
    upcomingQueueSlotCount: sessionResult.data?.upcoming_queue_slot_count ?? 1,
  };
}

/**
 * Whether to run the prune step that NULLs session_players rows no longer in the desired set.
 * Exported for unit tests (must match updateCourtAssignments behavior).
 */
export function shouldPruneCourtPlayersForSnapshot(playerUpdatesLength, deduplicatedAssignmentsLength) {
  return playerUpdatesLength > 0 || deduplicatedAssignmentsLength === 0;
}

// Serialize session_players writes per session to prevent deadlocks
const sessionPlayersWriteQueues = new Map();
const withSessionPlayersLock = (sessionId, task) => {
  const previous = sessionPlayersWriteQueues.get(sessionId) || Promise.resolve();
  const next = previous
    .catch(() => {}) // Don't block the queue on prior errors
    .then(task)
    .finally(() => {
      if (sessionPlayersWriteQueues.get(sessionId) === next) {
        sessionPlayersWriteQueues.delete(sessionId);
      }
    });
  sessionPlayersWriteQueues.set(sessionId, next);
  return next;
};

// ============================================================================
// LOADING FUNCTIONS - Convert normalized tables back to expected state shape
// ============================================================================

/**
 * Active member who is neither on the waitlist nor on a court roster in session_players.
 * Uses the same placement signals as buildCourtAssignments (court_type + court_number + team)
 * so we do not double-list someone whose player_list_status is stale but roster fields are set.
 */
function isOrphanedSessionPlayer(p) {
  if (p.status !== 'active') return false;
  if (p.player_list_status === 'waitlist') return false;
  const team = p.team_number;
  const onRoster =
    p.court_number != null &&
    (team === 1 || team === 2) &&
    (p.court_type === 'active' || p.court_type === 'next_up');
  return !onRoster;
}

/**
 * Build normalized client state from snapshot row arrays (same shape as legacy multi-fetch load).
 */
function buildNormalizedStateFromSnapshotRows({
  players,
  courts,
  interactions,
  categoryRanges,
  upcomingQueueSlotCountRaw,
}) {
  const playersArr = Array.isArray(players) ? players : [];
  const courtsArr = Array.isArray(courts) ? courts : [];
  const interactionsArr = Array.isArray(interactions) ? interactions : [];
  const categories = Array.isArray(categoryRanges) ? categoryRanges : [];

  const activeCourtNumbers = new Set();
  playersArr
    .filter(p => p.court_type === 'active' && p.court_number != null)
    .forEach(p => activeCourtNumbers.add(p.court_number));
  courtsArr
    .filter(c => c.court_type === 'active' && c.court_number != null)
    .forEach(c => activeCourtNumbers.add(c.court_number));

  const maxCourtNumber =
    activeCourtNumbers.size > 0 ? Math.max(...Array.from(activeCourtNumbers)) : 1;

  const waitlistIds = playersArr
    .filter(p => p.player_list_status === 'waitlist')
    .sort((a, b) => (a.waitlist_position ?? 999999) - (b.waitlist_position ?? 999999))
    .map(p => p.player_id);
  const waitlistSet = new Set(waitlistIds);
  const orphanIds = playersArr
    .filter(isOrphanedSessionPlayer)
    .filter(p => !waitlistSet.has(p.player_id))
    .sort(
      (a, b) =>
        new Date(a.joined_at || a.created_at || 0) -
        new Date(b.joined_at || b.created_at || 0)
    )
    .map(p => p.player_id);
  if (orphanIds.length > 0) {
    console.warn(
      'SessionStateService: merging orphaned active players into waitlist on load:',
      orphanIds.length
    );
  }

  return {
    waitingList: [...waitlistIds, ...orphanIds],
    orphanedWaitlistPlayerIds: orphanIds,
    courtAssignments: buildCourtAssignments(playersArr, courtsArr, 'active'),
    nextAssignments: buildCourtAssignments(playersArr, courtsArr, 'next_up'),
    upcomingQueueSlotCount:
      typeof upcomingQueueSlotCountRaw === 'number' && upcomingQueueSlotCountRaw >= 0
        ? Math.min(2, Math.max(0, Math.round(upcomingQueueSlotCountRaw)))
        : 1,
    maxCourtNumber,
    playerWaitMetrics: buildPlayerWaitMetrics(playersArr),
    playerInteractions: buildPlayerInteractions(interactionsArr),
    playerCategories: buildPlayerCategories(playersArr),
    categoryRanges: categories,
  };
}

/**
 * Persist orphaned active players onto the DB waitlist so server matchmaking and
 * courts_watched increments include them (mirrors client-side orphan merge on load).
 */
export async function repairOrphanedWaitlistPlayers(sessionId, orphanIds, tabInstanceId = null) {
  if (!sessionId || !Array.isArray(orphanIds) || orphanIds.length === 0) {
    return { repaired: 0 };
  }
  let repaired = 0;
  for (const playerId of orphanIds) {
    try {
      await addPlayerToWaitlistAtomic(sessionId, playerId, tabInstanceId);
      repaired += 1;
    } catch (err) {
      console.warn('[SessionStateService] repairOrphanedWaitlistPlayers failed for', playerId, err);
    }
  }
  if (repaired > 0) {
    console.log('[SessionStateService] Repaired orphaned waitlist players:', repaired);
  }
  return { repaired };
}

/**
 * Load complete session state from normalized tables
 * Returns state in the same shape as the old JSONB system for compatibility
 */
export async function loadNormalizedState(sessionId) {
  console.log('📊 SessionStateService: Loading normalized state for session:', sessionId);

  try {
    const { data, error } = await supabase.rpc('load_session_normalized_snapshot', {
      p_session_id: sessionId,
    });

    if (error) throw error;
    if (!data || data.ok !== true) {
      const msg =
        typeof data?.message === 'string' && data.message.length > 0
          ? data.message
          : 'Failed to load normalized session snapshot';
      const code = data?.code ? ` [${data.code}]` : '';
      throw new Error(`${msg}${code}`);
    }

    const state = buildNormalizedStateFromSnapshotRows({
      players: data.players,
      courts: data.courts,
      interactions: data.interactions,
      categoryRanges: data.category_ranges,
      upcomingQueueSlotCountRaw: data.upcoming_queue_slot_count,
    });

    console.log('✅ SessionStateService: Loaded normalized state (snapshot RPC):', {
      waitingList: state.waitingList.length,
      courtAssignments: state.courtAssignments.length,
      nextAssignments: state.nextAssignments.length,
      upcomingQueueSlotCount: state.upcomingQueueSlotCount,
      maxCourtNumber: state.maxCourtNumber,
      playerCount: (Array.isArray(data.players) ? data.players : []).length,
    });

    return state;
  } catch (error) {
    console.error('❌ SessionStateService: Error loading normalized state:', error);
    console.error('❌ SessionStateService: Error details:', {
      message: error?.message,
      stack: error?.stack,
      code: error?.code,
      details: error?.details,
      hint: error?.hint,
    });
    throw new Error(`Failed to load normalized session state: ${error?.message || 'Unknown error'}`, {
      cause: error,
    });
  }
}

/**
 * Build court assignments array from players and courts data
 * IMPORTANT: Includes empty courts from session_courts table to maintain state persistence
 */

function buildCourtAssignments(players, courts, courtType) {
  const courtMap = {};

  // First, create entries for ALL courts from session_courts table (including empty ones)
  // This ensures empty courts persist across page refreshes
  courts
    .filter(c => c.court_type === courtType && c.court_number != null)
    .forEach(c => {
      const key = c.court_number;
      if (!courtMap[key]) {
        courtMap[key] = {
          courtNumber: c.court_number,
          team1: [],
          team2: [],
          inProgress: c.in_progress || false,
          startTime: mapCourtMatchStartTime(c),
          version: c.version ?? 0,
          gameCreation: {
            origin: c.game_creation_origin || null,
            algorithm: c.game_creation_algorithm || null,
            isAltered: c.game_creation_altered || false,
            scheduledGameId: c.scheduled_game_id || null
          }
        };
      }
    });

  // Then, populate players into their respective courts
  players
    .filter(p => p.court_type === courtType && p.court_number != null)
    .forEach(p => {
      const key = p.court_number;
      if (!courtMap[key]) {
        // Court exists in players but not in session_courts - create it
        courtMap[key] = {
          courtNumber: p.court_number,
          team1: [],
          team2: [],
          inProgress: false,
          version: 0,
          gameCreation: {}
        };
      }

      if (p.team_number === 1) {
        courtMap[key].team1.push(p.player_id);
      } else if (p.team_number === 2) {
        courtMap[key].team2.push(p.player_id);
      }
    });

  Object.values(courtMap).forEach((court) => {
    court.team1.sort((a, b) => String(a).localeCompare(String(b)));
    court.team2.sort((a, b) => String(a).localeCompare(String(b)));
  });

  // Update court metadata for courts that have players (in case it changed)
  courts
    .filter(c => c.court_type === courtType)
    .forEach(c => {
      if (courtMap[c.court_number]) {
        courtMap[c.court_number].inProgress = c.in_progress;
        courtMap[c.court_number].startTime = mapCourtMatchStartTime(c);
        courtMap[c.court_number].version = c.version ?? 0;
        // Ensure gameCreation has all expected properties (even if null)
        courtMap[c.court_number].gameCreation = {
          origin: c.game_creation_origin || null,
          algorithm: c.game_creation_algorithm || null,
          isAltered: c.game_creation_altered || false,
          scheduledGameId: c.scheduled_game_id || null
        };
      }
    });

  // Ensure all courts have a gameCreation object and version field (even if empty)
  Object.values(courtMap).forEach(court => {
    if (court.version === undefined) court.version = 0;
    if (!court.gameCreation) {
      court.gameCreation = {
        origin: null,
        algorithm: null,
        isAltered: false, // Note: code expects 'isAltered', not 'altered'
        scheduledGameId: null
      };
    }
  });

  return Object.values(courtMap).sort((a, b) => a.courtNumber - b.courtNumber);
}

/**
 * Note: Player streaks are NOT stored in the database
 * They are calculated on-the-fly in the frontend from session_games
 * This ensures single source of truth and guaranteed consistency
 */

/**
 * Build player wait metrics map
 */
function buildPlayerWaitMetrics(players) {
  const metrics = {};
  players.forEach(p => {
    metrics[p.player_id] = {
      courtsObserved: p.courts_watched || 0,
      isOnCooldown: p.is_on_cooldown || false,
      lastCourtUpdate: p.last_court_update,
      lastGameCompletedAt: p.last_game_completed_at,
      matchPreference: p.match_preference || 'no_preference',
      preferenceStrength: p.preference_strength || 'soft',
      mensDoublesEligible: p.mens_doubles_eligible || false,
      mensDoublesShadowElo: p.mens_doubles_shadow_elo ?? null,
      /** From session_players.updated_at — used to merge snapshots without losing fresh preference rows to stale reads. */
      sessionPlayerRowUpdatedAt: p.updated_at || null,
    };
  });
  return metrics;
}

const parseWaitMetricRowTs = (t) => {
  if (t == null || t === '') return 0;
  const n = new Date(t).getTime();
  return Number.isFinite(n) ? n : 0;
};

/**
 * Cooldown fields are DB-authoritative (increment_courts_watched / finish_score).
 * Preference fields may be updated locally with a newer row timestamp — merge them separately
 * so a fresh preference write cannot resurrect a stale isOnCooldown flag.
 */
function mergeWaitMetricPlayerSlice(prevSlice, incomingSlice) {
  const p = prevSlice || {};
  const n = incomingSlice || {};
  const pT = parseWaitMetricRowTs(p.sessionPlayerRowUpdatedAt);
  const nT = parseWaitMetricRowTs(n.sessionPlayerRowUpdatedAt);
  const prefSource = pT > nT ? p : n;

  const prevObserved = Number(p.courtsObserved) || 0;
  const incomingObserved = Number(n.courtsObserved) || 0;
  const cooldownSource =
    incomingObserved > prevObserved
      ? n
      : prevObserved > incomingObserved
        ? p
        : p.isOnCooldown === false
          ? p
          : n.isOnCooldown === false
            ? n
            : nT >= pT
              ? n
              : p;

  return {
    ...p,
    ...n,
    ...prefSource,
    courtsObserved: Math.max(prevObserved, incomingObserved),
    isOnCooldown: Boolean(cooldownSource.isOnCooldown),
    lastCourtUpdate: cooldownSource.lastCourtUpdate ?? prefSource.lastCourtUpdate ?? null,
    lastGameCompletedAt: cooldownSource.lastGameCompletedAt ?? prefSource.lastGameCompletedAt ?? null,
    sessionPlayerRowUpdatedAt:
      pT >= nT ? p.sessionPlayerRowUpdatedAt : n.sessionPlayerRowUpdatedAt,
    matchPreference: prefSource.matchPreference ?? 'no_preference',
    preferenceStrength: prefSource.preferenceStrength ?? 'soft',
    // Eligibility follows same timestamp rule as preferences (host-written fields)
    mensDoublesEligible: prefSource.mensDoublesEligible ?? false,
    mensDoublesShadowElo: prefSource.mensDoublesShadowElo ?? null,
  };
}

/**
 * When applying a DB snapshot after local optimistic updates, a follow-up load can briefly
 * return an older session_players row (read lag). Prefer the slice with the newer
 * `sessionPlayerRowUpdatedAt` for preference fields; cooldown fields use courts_watched /
 * is_on_cooldown semantics instead of row timestamp alone.
 *
 * @param {object} [options.trustIncomingWaitMetrics] When true (authoritative post-score reload),
 *   replace wait metrics entirely from incoming — do not merge with local optimistic state.
 */
export function mergePlayerWaitMetricsSnapshots(prev, incoming, options = {}) {
  const { trustIncomingWaitMetrics = false } = options;
  const prevM = prev && typeof prev === 'object' ? prev : {};
  const incM = incoming && typeof incoming === 'object' ? incoming : {};

  if (trustIncomingWaitMetrics) {
    return { ...incM };
  }

  const ids = new Set([...Object.keys(prevM), ...Object.keys(incM)]);
  const out = {};
  for (const id of ids) {
    const p = prevM[id];
    const n = incM[id];
    if (!p && n) {
      out[id] = n;
      continue;
    }
    if (p && !n) {
      out[id] = p;
      continue;
    }
    out[id] = mergeWaitMetricPlayerSlice(p, n);
  }
  return out;
}

/**
 * Build player interactions map
 * Format: { "player1-player2": totalCount, "player1-player2_sameTeam": sameTeamCount }
 * Total = opponent_count + same_team_count
 */
function buildPlayerInteractions(interactions) {
  const map = {};
  interactions.forEach(int => {
    const key = [int.player1_id, int.player2_id].sort().join('-');
    
    // Calculate total: opponent_count + same_team_count
    const opponentCount = int.opponent_count || 0;
    const sameTeamCount = int.same_team_count || 0;
    const totalCount = opponentCount + sameTeamCount;
    
    // Store total interaction count (for backward compatibility with existing code)
    map[key] = totalCount;
    
    // Store same-team count if > 0
    if (sameTeamCount > 0) {
      map[`${key}_sameTeam`] = sameTeamCount;
    }
  });
  return map;
}

/**
 * Build player categories map
 */
function buildPlayerCategories(players) {
  const categories = {};
  players.forEach(p => {
    if (p.mixed_category || p.levels_category) {
      categories[p.player_id] = {
        mixed: p.mixed_category,
        levels: p.levels_category,
        calculatedAt: p.category_calculated_at
      };
    }
  });
  return categories;
}

// ============================================================================
// SAVING FUNCTIONS - Granular updates to normalized tables
// ============================================================================

// ============================================================================
// WAITING LIST — ATOMIC PER-PLAYER OPERATIONS
// ============================================================================

/**
 * Atomically append one player to the end of the waiting list.
 * Safe to call concurrently from multiple hosts.
 * Uses the add_player_to_waitlist Postgres RPC (MAX position + 1 inside one statement).
 *
 * @param {string}      sessionId     – session UUID
 * @param {string}      playerId      – player UUID to add
 * @param {string|null} tabInstanceId – writer lease tab ID; when provided the RPC verifies the lease
 * @returns {Promise<number>} the assigned waitlist_position
 */
export async function addPlayerToWaitlistAtomic(sessionId, playerId, tabInstanceId = null, commandId = null) {
  const { data, error } = await supabase.rpc('add_player_to_waitlist', {
    p_session_id:      sessionId,
    p_player_id:       playerId,
    p_tab_instance_id: tabInstanceId ?? null,
    p_command_id:      commandId ?? null,
  });
  if (error) {
    console.error('❌ SessionStateService: addPlayerToWaitlistAtomic error:', error);
    throw error;
  }
  return data; // assigned position number
}

/**
 * Atomically remove one player from the waiting list via the server-side RPC.
 * Uses the remove_player_from_waitlist RPC (with optional lease check).
 *
 * @param {string}      sessionId     – session UUID
 * @param {string}      playerId      – player UUID to remove
 * @param {string|null} tabInstanceId – writer lease tab ID; when provided the RPC verifies the lease
 */
export async function removePlayerFromWaitlistAtomic(sessionId, playerId, tabInstanceId = null, commandId = null) {
  const { data, error } = await supabase.rpc('remove_player_from_waitlist', {
    p_session_id:      sessionId,
    p_player_id:       playerId,
    p_tab_instance_id: tabInstanceId ?? null,
    p_command_id:      commandId ?? null,
  });

  if (error) {
    console.error('❌ SessionStateService: removePlayerFromWaitlistAtomic error:', error);
    throw error;
  }

  if (data?.not_writer) {
    console.warn('❌ SessionStateService: removePlayerFromWaitlistAtomic — lease not held:', data?.code);
    throw new Error(`NOT_WRITER: ${data?.message ?? 'Writer lease check failed'}`);
  }
}

/**
 * Atomically reassign waiting-list positions after a manual drag-to-reorder.
 * Uses the reorder_waitlist Postgres RPC which runs all position updates in
 * a single transaction so no partial states are visible to other hosts.
 *
 * @param {string}      sessionId        – session UUID
 * @param {string[]}    orderedPlayerIds – player IDs in the desired display order
 * @param {string|null} tabInstanceId    – writer lease tab ID; when provided the RPC verifies the lease
 */
export async function reorderWaitlistAtomic(sessionId, orderedPlayerIds, tabInstanceId = null, commandId = null) {
  const { error } = await supabase.rpc('reorder_waitlist', {
    p_session_id:      sessionId,
    p_player_ids:      orderedPlayerIds.filter(Boolean),
    p_tab_instance_id: tabInstanceId ?? null,
    p_command_id:      commandId ?? null,
  });
  if (error) {
    console.error('❌ SessionStateService: reorderWaitlistAtomic error:', error);
    throw error;
  }
}

/**
 * Update waiting list (batch update player_list_status)
 */
export async function updateWaitingList(sessionId, playerIds) {
  const uniquePlayerIds = Array.from(new Set((playerIds || []).filter(Boolean)));
  persistDebugLog(sessionId, 'update_waiting_list', { count: uniquePlayerIds.length });

  return withSessionPlayersLock(sessionId, async () => {
    try {
      // First, clear all players from waitlist (including positions)
      await supabase
        .from('session_players')
        .update({ 
          player_list_status: null,
          waitlist_position:  null,
          court_number:       null,
          court_type:         null,
          team_number:        null,
          updated_at:         new Date().toISOString()
        })
        .eq('session_id', sessionId)
        .eq('player_list_status', 'waitlist');

      // Then set the new waiting list, preserving order via waitlist_position
      if (uniquePlayerIds.length > 0) {
        const updates = uniquePlayerIds.map((playerId, index) => ({
          session_id:         sessionId,
          player_id:          playerId,
          player_list_status: 'waitlist',
          waitlist_position:  index + 1,   // 1-based, matches array order
          court_number:       null,
          court_type:         null,
          team_number:        null,
          updated_at:         new Date().toISOString()
        }));

        // Use upsert to handle cases where player might not exist
        const { error } = await supabase
          .from('session_players')
          .upsert(updates, { 
            onConflict: 'session_id,player_id',
            ignoreDuplicates: false 
          });

        if (error) throw error;
      }

      persistDebugLog(sessionId, 'waiting_list_updated', { count: uniquePlayerIds.length });
    } catch (error) {
      console.error('❌ SessionStateService: Error updating waiting list:', error);
      console.error('❌ Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        fullError: JSON.stringify(error, null, 2)
      });
      console.error('❌ Updates that failed:', playerIds);
      throw error;
    }
  });
}

/**
 * Update court assignments (both players and court metadata)
 * @param {string} sessionId - Session ID
 * @param {Array} assignments - Court assignments
 * @param {string} courtType - Court type ('active' or 'next_up')
 * @param {boolean} isSocialSession - Whether this is a social session (skips game_creation_origin tracking)
 */
export async function updateCourtAssignments(sessionId, assignments, courtType = 'active', isSocialSession = false) {
  persistDebugLog(sessionId, 'update_court_assignments_start', {
    courts: assignments.length,
    courtType,
    isSocialSession,
  });

  let playerUpdates = [];
  let courtInserts = [];

  return withSessionPlayersLock(sessionId, async () => {
    try {
    // IMPORTANT: Do NOT blanket-clear all session_players for this court_type before upserting.
    // A debounced save can briefly hold court *shells* (courtNumber + empty team1/team2). Clearing
    // first would NULL every on-court row and then playerUpdates stays empty — mass data loss.

    // Delete existing court records of this type only if we have new assignments
    // If assignments is empty, we'll delete all courts of this type after processing
    // If assignments has items, we'll upsert (which will update existing or create new)

    // DEDUPLICATE: Remove duplicate court assignments (same courtNumber + courtType)
    // Keep the first occurrence of each unique court
    const seenCourts = new Map();
    const deduplicatedAssignments = [];
    assignments.forEach((assignment, index) => {
      let { courtNumber } = assignment;
      
      // Auto-assign courtNumber if missing (for next_up)
      // For active courts, courtNumber must be provided
      if (courtNumber === undefined || courtNumber === null) {
        if (courtType === 'next_up') {
          // For next_up, use index as court number
          courtNumber = index;
        } else {
          // For active courts, courtNumber is required
          console.error('❌ SessionStateService: Invalid courtNumber in active court assignment:', assignment);
          throw new Error(`Invalid courtNumber for active court at index ${index}. Assignment must have a valid courtNumber.`);
        }
      }

      const courtKey = `${courtType}-${courtNumber}`;
      if (seenCourts.has(courtKey)) {
        const existingIndex = seenCourts.get(courtKey);
        console.warn(`⚠️ SessionStateService: Duplicate court assignment detected at index ${index} (courtNumber=${courtNumber}, courtType=${courtType}). Keeping first occurrence at index ${existingIndex}, skipping duplicate.`);
        return; // Skip this duplicate
      }
      
      seenCourts.set(courtKey, index);
      // Ensure courtNumber is set in the assignment object for consistency
      const assignmentWithCourtNumber = { ...assignment, courtNumber };
      deduplicatedAssignments.push(assignmentWithCourtNumber);
    });

    // For next_up courts, renumber sequentially after deduplication
    // This ensures court numbers are 0, 1, 2... without gaps
    if (courtType === 'next_up') {
      deduplicatedAssignments.forEach((assignment, index) => {
        assignment.courtNumber = index;
      });
    }

    if (deduplicatedAssignments.length !== assignments.length) {
      persistDebugLog(sessionId, 'court_assignments_deduped', {
        from: assignments.length,
        to: deduplicatedAssignments.length,
        courtType,
      });
    }

    // Insert new assignments
    playerUpdates = [];
    courtInserts = [];

    deduplicatedAssignments.forEach((assignment, index) => {
      let { courtNumber, team1, team2, inProgress, gameCreation } = assignment;

      // Auto-assign courtNumber if missing (use index for next_up, must be provided for active)
      if (courtNumber === undefined || courtNumber === null) {
        if (courtType === 'next_up') {
          // For next_up courts, use the array index as court number (0, 1, 2...)
          // After deduplication, indices are sequential
          courtNumber = index;
          console.log(`📝 SessionStateService: Auto-assigned courtNumber ${courtNumber} for next_up court at index ${index}`);
        } else {
          // For active courts, courtNumber should already be set (from deduplication step)
          // If it's still missing, that's an error
          console.error('❌ SessionStateService: Invalid courtNumber in active court assignment:', assignment);
          throw new Error(`Invalid courtNumber for active court at index ${index}. Assignment must have a valid courtNumber.`);
        }
      }

      // Validate team arrays
      const team1Array = Array.isArray(team1) ? team1 : [];
      const team2Array = Array.isArray(team2) ? team2 : [];

      // Add team 1 players
      team1Array.forEach(playerId => {
        if (!playerId) {
          console.warn('⚠️ SessionStateService: Skipping null/undefined playerId in team1');
          return;
        }
        playerUpdates.push({
          session_id: sessionId,
          player_id: playerId,
          player_list_status: courtType === 'active' ? 'on_court' : 'next_up',
          court_number: courtNumber,
          court_type: courtType,
          team_number: 1,
          updated_at: new Date().toISOString()
        });
      });

      // Add team 2 players
      team2Array.forEach(playerId => {
        if (!playerId) {
          console.warn('⚠️ SessionStateService: Skipping null/undefined playerId in team2');
          return;
        }
        playerUpdates.push({
          session_id: sessionId,
          player_id: playerId,
          player_list_status: courtType === 'active' ? 'on_court' : 'next_up',
          court_number: courtNumber,
          court_type: courtType,
          team_number: 2,
          updated_at: new Date().toISOString()
        });
      });

      // For social sessions, skip game_creation_origin tracking (not needed for logging feature)
      // For competitive sessions, validate and track game_creation_origin
      // If gameCreation is not provided, assume it's a social session or simple assignment
      let gameCreationOrigin = null;
      let gameCreationAlgorithm = null;
      let gameCreationAltered = false;
      
      if (!isSocialSession && gameCreation && gameCreation.origin) {
        gameCreationOrigin = gameCreation.origin;
        if (!['matchmaking', 'scheduled', 'authoritative'].includes(gameCreationOrigin)) {
          console.warn(`⚠️ SessionStateService: Invalid game_creation_origin: ${gameCreationOrigin}, setting to null`);
          gameCreationOrigin = null;
        } else {
          gameCreationAlgorithm = gameCreation?.algorithm || null;
          gameCreationAltered = gameCreation?.isAltered || false;
        }
      }

      // Add court metadata (only if there are players assigned)
      // Even empty courts should have a record if they exist
      courtInserts.push({
        session_id: sessionId,
        court_number: courtNumber,
        court_type: courtType,
        in_progress: inProgress || false,
        game_creation_origin: gameCreationOrigin,
        game_creation_algorithm: gameCreationAlgorithm,
        game_creation_altered: gameCreationAltered,
        updated_at: new Date().toISOString()
      });
    });

    const desiredPlayerIds = [...new Set(playerUpdates.map((u) => u.player_id).filter(Boolean))];

    // Apply court / next-up placements first, then strip rows that still have this court_type
    // but are no longer in the desired set (safe empty-courts case: desired empty → prune all).
    if (playerUpdates.length > 0) {
      const { error: playerError } = await supabase
        .from('session_players')
        .upsert(playerUpdates, { 
          onConflict: 'session_id,player_id',
          ignoreDuplicates: false 
        });

      if (playerError) throw playerError;
    }

    // If we have court *shells* but zero player rows to upsert, pruning would wipe everyone
    // still marked with this court_type — the usual stale-save / race that emptied your DB.
    // Only prune when we actually have placements, or when assignments[] is empty (drop all courts).
    const shouldPruneCourtPlayers = shouldPruneCourtPlayersForSnapshot(
      playerUpdates.length,
      deduplicatedAssignments.length
    );

    if (shouldPruneCourtPlayers) {
      const clearCourtPayload = {
        player_list_status: null,
        court_number: null,
        court_type: null,
        team_number: null,
        updated_at: new Date().toISOString(),
      };
      let pruneQuery = supabase
        .from('session_players')
        .update(clearCourtPayload)
        .eq('session_id', sessionId)
        .eq('court_type', courtType);
      if (desiredPlayerIds.length > 0) {
        pruneQuery = pruneQuery.not('player_id', 'in', `(${desiredPlayerIds.join(',')})`);
      }
      const { error: pruneError } = await pruneQuery;
      if (pruneError) throw pruneError;
    } else {
      persistDebugWarn(sessionId, 'prune_skipped_shell_only', {
        courtType,
        courtShellCount: deduplicatedAssignments.length,
      });
      console.error(
        '🚨 SessionStateService: Shell-only save detected — court shells present but zero player rows.',
        'This should not happen after the partial-save removal. Investigate the snapshot.',
        { sessionId, courtType, courtShellCount: deduplicatedAssignments.length }
      );
    }

    // Batch insert courts
    if (courtInserts.length > 0) {
      // Check for duplicate court numbers (would violate UNIQUE constraint)
      const courtKeys = new Set();
      const duplicates = [];
      courtInserts.forEach((court, idx) => {
        const key = `${court.court_type}-${court.court_number}`;
        if (courtKeys.has(key)) {
          duplicates.push({ index: idx, court });
        }
        courtKeys.add(key);
      });

      if (duplicates.length > 0) {
        console.error('❌ SessionStateService: Duplicate court numbers detected:', duplicates);
        throw new Error(`Duplicate court assignments detected: ${duplicates.map(d => `court_type=${d.court.court_type}, court_number=${d.court.court_number}`).join(', ')}`);
      }

      persistDebugLog(sessionId, 'session_courts_upsert', {
        courtType,
        count: courtInserts.length,
        keys: courtInserts.map(c => `${c.court_type}:${c.court_number}`),
      });

      // Use upsert instead of insert to handle cases where court might already exist
      // This is safer than delete + insert, especially with concurrent operations
      const { data, error: courtError } = await supabase
        .from('session_courts')
        .upsert(courtInserts, {
          onConflict: 'session_id,court_type,court_number',
          ignoreDuplicates: false
        })
        .select();

      if (courtError) {
        console.error('❌ SessionStateService: Court upsert error details:', {
          message: courtError.message,
          details: courtError.details,
          hint: courtError.hint,
          code: courtError.code,
          data: courtInserts
        });
        throw courtError;
      }

      persistDebugLog(sessionId, 'session_courts_upsert_ok', { rows: data?.length || 0 });
    } else {
      // If no court inserts, delete all courts of this type (cleanup)
      const { error: deleteError } = await supabase
        .from('session_courts')
        .delete()
        .eq('session_id', sessionId)
        .eq('court_type', courtType);
      
      if (deleteError) {
        console.error('❌ SessionStateService: Error deleting empty courts:', deleteError);
        throw deleteError;
      }
      persistDebugLog(sessionId, 'session_courts_deleted_type', { courtType });
    }

      persistDebugLog(sessionId, 'court_assignments_done', { courtType });
    } catch (error) {
      console.error('❌ SessionStateService: Error updating court assignments:', error);
      console.error('❌ Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        fullError: JSON.stringify(error, null, 2)
      });
      console.error('❌ Player updates that failed:', playerUpdates?.slice(0, 3));
      console.error('❌ Court inserts that failed:', courtInserts?.slice(0, 3));
      throw error;
    }
  });
}

/**
 * Note: updatePlayerStreaks removed - streaks are calculated on-the-fly from session_games
 * No need to store them separately as they're derived data
 */

/**
 * Update player wait metrics (cooldown tracking)
 */
export async function updatePlayerWaitMetrics(sessionId, playerMetricsMap) {
  console.log('📝 SessionStateService: Updating player wait metrics:', Object.keys(playerMetricsMap).length, 'players');

  return withSessionPlayersLock(sessionId, async () => {
    try {
    const updates = Object.entries(playerMetricsMap).map(([playerId, metrics]) => {
      // Parse timestamps if they're numbers (epoch time)
      let lastCourtUpdate = metrics.lastCourtUpdate;
      let lastGameCompletedAt = metrics.lastGameCompletedAt;
      
      if (typeof lastCourtUpdate === 'number') {
        lastCourtUpdate = new Date(lastCourtUpdate).toISOString();
      }
      
      if (typeof lastGameCompletedAt === 'number') {
        lastGameCompletedAt = new Date(lastGameCompletedAt).toISOString();
      }

      return {
        session_id: sessionId,
        player_id: playerId,
        courts_watched: metrics.courtsObserved || 0,
        is_on_cooldown: Boolean(metrics.isOnCooldown), // Explicitly convert to boolean
        last_court_update: lastCourtUpdate || null,
        last_game_completed_at: lastGameCompletedAt || null,
        updated_at: new Date().toISOString()
      };
    });

    console.log('📝 SessionStateService: Sample wait metric update:', updates[0]);
    console.log('📝 SessionStateService: Players with cooldown:', 
      updates.filter(u => u.is_on_cooldown).map(u => u.player_id));

    const { data, error } = await supabase
      .from('session_players')
      .upsert(updates, { 
        onConflict: 'session_id,player_id',
        ignoreDuplicates: false 
      })
      .select();

    if (error) {
      console.error('❌ SessionStateService: Wait metrics upsert error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        sampleData: updates[0]
      });
      throw error;
    }

      console.log('✅ SessionStateService: Player wait metrics updated');
    } catch (error) {
      console.error('❌ SessionStateService: Error updating player wait metrics:', error);
      throw error;
    }
  });
}

/**
 * Increment courts_watched for waiting players
 */
export async function incrementCourtsWatched(sessionId, playerIds) {
  console.log('📝 SessionStateService: Incrementing courts_watched for', playerIds.length, 'players');

  return withSessionPlayersLock(sessionId, async () => {
    try {
      // Use PostgreSQL to increment atomically (no race conditions!)
      const { error } = await supabase.rpc('increment_courts_watched', {
        p_session_id: sessionId,
        p_player_ids: playerIds
      });

      if (error) {
        // If function doesn't exist, fall back to manual increment
        console.warn('⚠️ RPC function not found, using fallback method');
        
        for (const playerId of playerIds) {
          const { data, error: fetchError } = await supabase
            .from('session_players')
            .select('courts_watched')
            .eq('session_id', sessionId)
            .eq('player_id', playerId)
            .single();

          if (fetchError) throw fetchError;

          const newCount = (data.courts_watched || 0) + 1;

          const { error: updateError } = await supabase
            .from('session_players')
            .update({ 
              courts_watched: newCount,
              is_on_cooldown: false, // Clear cooldown when they've watched a court
              last_court_update: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('session_id', sessionId)
            .eq('player_id', playerId);

          if (updateError) throw updateError;
        }
      }

      console.log('✅ SessionStateService: Courts watched incremented');
    } catch (error) {
      console.error('❌ SessionStateService: Error incrementing courts watched:', error);
      throw error;
    }
  });
}

/**
 * Reset courts_watched after player plays
 */
export async function resetCourtsWatched(sessionId, playerIds) {
  console.log('📝 SessionStateService: Resetting courts_watched for', playerIds.length, 'players');

  return withSessionPlayersLock(sessionId, async () => {
    try {
      const { error } = await supabase
        .from('session_players')
        .update({ 
          courts_watched: 0,
          is_on_cooldown: true, // Set cooldown when they just played
          last_game_completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('session_id', sessionId)
        .in('player_id', playerIds);

      if (error) throw error;

      console.log('✅ SessionStateService: Courts watched reset, cooldown activated');
    } catch (error) {
      console.error('❌ SessionStateService: Error resetting courts watched:', error);
      throw error;
    }
  });
}

/**
 * Update player interactions (increment or create)
 * Uses database function to atomically increment interaction count
 * @param {string} sessionId - Session ID
 * @param {string} player1Id - First player ID
 * @param {string} player2Id - Second player ID
 * @param {boolean} isSameTeam - Whether they played on the same team
 */
export async function updatePlayerInteractions(sessionId, player1Id, player2Id, isSameTeam = false) {
  console.log('📝 SessionStateService: Updating interaction:', player1Id, '<->', player2Id, isSameTeam ? '(same team)' : '(opponents)');

  try {
    // Use database function to atomically increment (handles ordering automatically)
    const { error } = await supabase.rpc('increment_player_interaction', {
      p_session_id: sessionId,
      p_player1_id: player1Id,
      p_player2_id: player2Id,
      p_is_same_team: isSameTeam
    });

    if (error) throw error;

    console.log('✅ SessionStateService: Player interaction updated');
  } catch (error) {
    console.error('❌ SessionStateService: Error updating player interaction:', error);
    throw error;
  }
}

/**
 * Batch update player interactions after a game
 * Uses database function to atomically increment interaction counts
 * @param {string} sessionId - Session ID
 * @param {Array} playerPairs - Array of { player1Id, player2Id, isSameTeam }
 */
export async function batchUpdatePlayerInteractions(sessionId, playerPairs) {
  console.log('📝 SessionStateService: Batch updating', playerPairs.length, 'player interactions');

  try {
    // Call database function for each pair to atomically increment
    // The function handles player ordering automatically
    const promises = playerPairs.map(({ player1Id, player2Id, isSameTeam = false }) =>
      supabase.rpc('increment_player_interaction', {
        p_session_id: sessionId,
        p_player1_id: player1Id,
        p_player2_id: player2Id,
        p_is_same_team: isSameTeam
      })
    );

    const results = await Promise.all(promises);
    
    // Check for any errors
    const errors = results.filter(r => r.error);
    if (errors.length > 0) {
      throw new Error(`Failed to update ${errors.length} interactions: ${errors[0].error.message}`);
    }

    console.log('✅ SessionStateService: Batch player interactions updated');
  } catch (error) {
    console.error('❌ SessionStateService: Error batch updating player interactions:', error);
    throw error;
  }
}

/**
 * Update player categories
 */
export async function updatePlayerCategories(sessionId, playerCategoriesMap, categoryRanges) {
  console.log('📝 SessionStateService: Updating player categories:', Object.keys(playerCategoriesMap).length, 'players');

  try {
    const timestamp = new Date().toISOString();

    // Update player categories
    const playerUpdates = Object.entries(playerCategoriesMap).map(([playerId, categories]) => ({
      session_id: sessionId,
      player_id: playerId,
      mixed_category: categories.mixed || null,
      levels_category: categories.levels || null,
      category_calculated_at: timestamp,
      updated_at: timestamp
    }));

    const { error: playerError } = await supabase
      .from('session_players')
      .upsert(playerUpdates, { 
        onConflict: 'session_id,player_id',
        ignoreDuplicates: false 
      });

    if (playerError) throw playerError;

    // Store category ranges for historical reference
    if (categoryRanges && categoryRanges.length > 0) {
      const rangeInserts = categoryRanges.map(range => ({
        session_id: sessionId,
        cohort_type: range.cohort_type,
        category_label: range.category_label,
        min_elo: range.min_elo,
        max_elo: range.max_elo,
        player_count: range.player_count,
        calculated_at: timestamp
      }));

      // Delete old ranges first
      await supabase
        .from('session_category_ranges')
        .delete()
        .eq('session_id', sessionId);

      const { error: rangeError } = await supabase
        .from('session_category_ranges')
        .insert(rangeInserts);

      if (rangeError) throw rangeError;
    }

    console.log('✅ SessionStateService: Player categories updated');
  } catch (error) {
    console.error('❌ SessionStateService: Error updating player categories:', error);
    throw error;
  }
}

/**
 * Save complete session state (used for full replacements)
 * This is similar to the old JSONB approach but uses granular updates
 */
export async function saveCompleteState(sessionId, state, isSocialSession = false) {
  console.log('📝 SessionStateService: Saving complete state for session:', sessionId);

  try {
    // Save all components in parallel
    // Note: playerStreaks removed - calculated on-the-fly from session_games
    await Promise.all([
      state.waitingList ? updateWaitingList(sessionId, state.waitingList) : Promise.resolve(),
      state.courtAssignments ? updateCourtAssignments(sessionId, state.courtAssignments, 'active', isSocialSession) : Promise.resolve(),
      state.nextAssignments ? updateCourtAssignments(sessionId, state.nextAssignments, 'next_up', isSocialSession) : Promise.resolve(),
      state.playerWaitMetrics ? updatePlayerWaitMetrics(sessionId, state.playerWaitMetrics) : Promise.resolve()
    ]);

    console.log('✅ SessionStateService: Complete state saved');
  } catch (error) {
    console.error('❌ SessionStateService: Error saving complete state:', error);
    throw error;
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a session is using normalized state
 */
export async function isUsingNormalizedState(sessionId) {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('use_normalized_state')
      .eq('id', sessionId)
      .single();

    if (error) throw error;

    return data?.use_normalized_state === true;
  } catch (error) {
    console.error('❌ SessionStateService: Error checking normalized state flag:', error);
    return false; // Default to old system if error
  }
}

/**
 * Count active courts from database (derives court count from session_courts table)
 * This is the source of truth for court count
 */
export async function getActiveCourtCount(sessionId) {
  try {
    const { data, error } = await supabase
      .from('session_courts')
      .select('court_number')
      .eq('session_id', sessionId)
      .eq('court_type', 'active');

    if (error) throw error;

    // Get unique court numbers and return the count
    const uniqueCourtNumbers = new Set((data || []).map(c => c.court_number));
    const count = uniqueCourtNumbers.size;
    
    console.log('📊 SessionStateService: Active court count from DB:', count, 'courts:', Array.from(uniqueCourtNumbers).sort((a, b) => a - b));
    return count;
  } catch (error) {
    console.error('❌ SessionStateService: Error getting active court count:', error);
    throw error;
  }
}

/**
 * Get the highest court number from database
 */
export async function getHighestCourtNumber(sessionId) {
  try {
    const { data, error } = await supabase
      .from('session_courts')
      .select('court_number')
      .eq('session_id', sessionId)
      .eq('court_type', 'active')
      .order('court_number', { ascending: false })
      .limit(1);

    if (error) throw error;

    const highestNumber = data && data.length > 0 ? data[0].court_number : 0;
    console.log('📊 SessionStateService: Highest court number from DB:', highestNumber);
    return highestNumber;
  } catch (error) {
    console.error('❌ SessionStateService: Error getting highest court number:', error);
    throw error;
  }
}

/**
 * Check if the highest court can be removed (in_progress = false AND no players)
 * Returns { canRemove: boolean, highestCourtNumber: number, reason: string }
 * This ensures users can only reduce courts if the highest court is empty and not in progress
 */
export async function canRemoveHighestCourt(sessionId) {
  try {
    // Get the highest court with its in_progress status
    const { data: highestCourt, error: courtError } = await supabase
      .from('session_courts')
      .select('court_number, in_progress')
      .eq('session_id', sessionId)
      .eq('court_type', 'active')
      .order('court_number', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (courtError) throw courtError;

    if (!highestCourt) {
      return {
        canRemove: false,
        highestCourtNumber: 0,
        reason: 'No courts exist to remove'
      };
    }

    const highestCourtNumber = highestCourt.court_number;

    // Check if highest court has in_progress = true
    if (highestCourt.in_progress === true) {
      return {
        canRemove: false,
        highestCourtNumber,
        reason: `Court ${highestCourtNumber} has a game in progress. Finish the game first.`
      };
    }

    // Check if highest court has players assigned
    const { data: players, error: playersError } = await supabase
      .from('session_players')
      .select('player_id')
      .eq('session_id', sessionId)
      .eq('court_type', 'active')
      .eq('court_number', highestCourtNumber)
      .limit(1);

    if (playersError) throw playersError;

    if (players && players.length > 0) {
      return {
        canRemove: false,
        highestCourtNumber,
        reason: `Court ${highestCourtNumber} has players assigned. Remove players first.`
      };
    }

    // Check if this is the only court (prevent reducing to 0 courts)
    const activeCourtCount = await getActiveCourtCount(sessionId);
    if (activeCourtCount <= 1) {
      return {
        canRemove: false,
        highestCourtNumber,
        reason: 'Sessions must have at least 1 court. Cannot remove the last court.'
      };
    }

    // Highest court is empty and not in progress - can be removed
    return {
      canRemove: true,
      highestCourtNumber,
      reason: null
    };
  } catch (error) {
    console.error('❌ SessionStateService: Error checking if highest court can be removed:', error);
    throw error;
  }
}

/**
 * Find the highest empty court (no players assigned AND in_progress = false) from database
 * Returns the court number of the highest empty court, or null if none found
 * When reducing courts, we always remove the highest empty court to prevent it from reappearing on reload
 * Uses in_progress column from session_courts table to determine if court is available
 */
export async function findHighestEmptyCourt(sessionId) {
  try {
    // Get all active courts from database with in_progress status, ordered by court_number descending
    const { data: courts, error: courtsError } = await supabase
      .from('session_courts')
      .select('court_number, in_progress')
      .eq('session_id', sessionId)
      .eq('court_type', 'active')
      .order('court_number', { ascending: false });

    if (courtsError) throw courtsError;

    if (!courts || courts.length === 0) {
      return null;
    }

    // Get all players assigned to active courts
    const { data: players, error: playersError } = await supabase
      .from('session_players')
      .select('court_number')
      .eq('session_id', sessionId)
      .eq('court_type', 'active')
      .not('court_number', 'is', null);

    if (playersError) throw playersError;

    // Find courts that have no players assigned AND in_progress = false
    const courtsWithPlayers = new Set((players || []).map(p => p.court_number));
    
    // Since courts are already sorted descending, find the first (highest) empty court
    const highestEmptyCourt = courts.find(c => 
      !courtsWithPlayers.has(c.court_number) && c.in_progress === false
    );

    if (!highestEmptyCourt) {
      return null;
    }

    const emptyCourtNumber = highestEmptyCourt.court_number;
    console.log('📊 SessionStateService: Found highest empty court:', emptyCourtNumber, '(from database in_progress check)');
    return emptyCourtNumber;
  } catch (error) {
    console.error('❌ SessionStateService: Error finding highest empty court:', error);
    throw error;
  }
}

/**
 * Delete a specific court from database (removes court record, not players)
 * This allows gaps in court numbering (e.g., 1, 2, 4 is valid)
 */
export async function deleteCourt(sessionId, courtNumber) {
  console.log('🗑️ SessionStateService: Deleting court', courtNumber, 'from database');

  try {
    // Delete the court record (players are already moved to waitlist by caller)
    const { error } = await supabase
      .from('session_courts')
      .delete()
      .eq('session_id', sessionId)
      .eq('court_type', 'active')
      .eq('court_number', courtNumber);

    if (error) throw error;

    console.log('✅ SessionStateService: Court', courtNumber, 'deleted from database');
  } catch (error) {
    console.error('❌ SessionStateService: Error deleting court:', error);
    throw error;
  }
}

/**
 * Remove one next-up queue slot: anyone still assigned to that slot is appended to the waitlist,
 * then the session_courts row is deleted. Per-slot atomic saves never delete extra shells, so the
 * host must call this when reducing queue slot count (or rely on the debounced removed-slot pass).
 * Idempotent if the row is already gone.
 */
/**
 * Persist host upcoming queue depth (0–2) and reconcile session_courts next_up rows via DB RPC.
 */
export async function setSessionUpcomingQueueSlotCount(sessionId, slots) {
  if (sessionId == null || slots == null) {
    throw new Error('setSessionUpcomingQueueSlotCount: missing sessionId or slots');
  }
  const { data, error } = await supabase.rpc('set_session_upcoming_queue_slot_count', {
    p_session_id: sessionId,
    p_slots: slots,
  });
  if (error) throw error;
  if (data && data.ok === false) {
    const msg = data.message || data.code || 'set_session_upcoming_queue_slot_count failed';
    throw new Error(msg);
  }
  return data;
}

export async function deleteNextUpCourtSlot(sessionId, courtNumber) {
  if (sessionId == null || courtNumber == null) {
    console.warn('SessionStateService: deleteNextUpCourtSlot skipped — missing sessionId or courtNumber');
    return;
  }

  persistDebugLog(sessionId, 'delete_next_up_slot_start', { courtNumber });

  return withSessionPlayersLock(sessionId, async () => {
    const { data: roster, error: rosterErr } = await supabase
      .from('session_players')
      .select('player_id')
      .eq('session_id', sessionId)
      .eq('court_type', 'next_up')
      .eq('court_number', courtNumber);

    if (rosterErr) throw rosterErr;

    for (const row of roster || []) {
      const { error: rpcErr } = await supabase.rpc('add_player_to_waitlist', {
        p_session_id: sessionId,
        p_player_id: row.player_id,
      });
      if (rpcErr) throw rpcErr;
    }

    const { error: delErr } = await supabase
      .from('session_courts')
      .delete()
      .eq('session_id', sessionId)
      .eq('court_type', 'next_up')
      .eq('court_number', courtNumber);

    if (delErr) throw delErr;

    persistDebugLog(sessionId, 'delete_next_up_slot_done', { courtNumber });
  });
}

/**
 * Find the first gap in court numbers (e.g., if courts 1, 2, 4 exist, returns 3)
 * Returns null if no gaps found (all courts are sequential starting from 1)
 */
export async function findFirstCourtGap(sessionId) {
  try {
    const { data: courts, error } = await supabase
      .from('session_courts')
      .select('court_number')
      .eq('session_id', sessionId)
      .eq('court_type', 'active')
      .order('court_number', { ascending: true });

    if (error) throw error;

    if (!courts || courts.length === 0) {
      return 1; // No courts exist, first gap is 1
    }

    const courtNumbers = courts.map(c => c.court_number).sort((a, b) => a - b);
    
    // Find first gap starting from 1
    for (let i = 1; i <= courtNumbers.length; i++) {
      if (courtNumbers[i - 1] !== i) {
        console.log('📊 SessionStateService: Found gap at court number', i);
        return i;
      }
    }

    // No gaps found - all courts are sequential
    return null;
  } catch (error) {
    console.error('❌ SessionStateService: Error finding court gap:', error);
    throw error;
  }
}

/**
 * Restore or create a court at a specific number
 * If the court exists in DB (soft-deleted), restore it; otherwise create new empty court
 */
export async function restoreOrCreateCourt(sessionId, courtNumber) {
  console.log('➕ SessionStateService: Restoring/creating court', courtNumber);

  try {
    // Check if court already exists
    const { data: existingCourt, error: checkError } = await supabase
      .from('session_courts')
      .select('*')
      .eq('session_id', sessionId)
      .eq('court_type', 'active')
      .eq('court_number', courtNumber)
      .maybeSingle();

    if (checkError) throw checkError;

    if (existingCourt) {
      console.log('✅ SessionStateService: Court', courtNumber, 'already exists in database');
      return existingCourt;
    }

    // Create new empty court
    const { data: newCourt, error: createError } = await supabase
      .from('session_courts')
      .insert({
        session_id: sessionId,
        court_type: 'active',
        court_number: courtNumber,
        game_creation_algorithm: null,
        game_creation_altered: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (createError) throw createError;

    console.log('✅ SessionStateService: Created new empty court', courtNumber);
    return newCourt;
  } catch (error) {
    console.error('❌ SessionStateService: Error restoring/creating court:', error);
    throw error;
  }
}

